// ============================================================
// PROBABILITY ENGINE - Combat statistics calculator.
// Hybrid analytical (hits) + Monte Carlo (damage/crits/WTK).
// Pure TypeScript, zero React dependency.
// ============================================================
import { helplessAgainst } from './helpless';
import { getAttackConditionModifiers } from './conditionModifiers';
import { exposedDos, onHitExposure, attackGate } from './exposures';
import type { OnHitExposure } from './exposures';
import { areaOf } from './areaShapes';
import { attackingMagnitude } from './bodyShape';
import type { ConditionModOptions, ConditionModContext } from './conditionModifiers';
import { applyUnnaturalDos, DEFAULT_UNNATURAL_DOS_MODE } from './unnaturalDos';
import type { UnnaturalDosMode } from './unnaturalDos';
import { calcDoS, withDiceSource } from './dice';
import { calcModifiedSkill, calcJamThreshold, calcTotalHits, calcEffectivePen, hitMode } from './attackCalc';
import { rollBurst } from './attackResolution';
import { afterHitDamage } from './afterHitDamage';
import type { HitResult } from './damageCalc';
import { getBodyConfig } from './bodyConfig';
import { burstLocations } from './multipleHits';
import { accurateBonusDice } from './weaponSpecials';
import type { Slot, Target } from '../types';
// The forecasts' own dice (engine/mulberry32): deterministic, never the real dice.
import { mulberry32 } from './mulberry32';

// ────────────────────────────────────────────────────────────
// Types
// ────────────────────────────────────────────────────────────

/** Probability of exactly N hits occurring. */
export interface HitDistribution {
  /** Index = hit count, value = probability [0,1]. */
  probabilities: number[];
  /** Expected number of hits. */
  expectedHits: number;
  /** Probability of at least one hit. */
  hitChance: number;
  /** Probability of a jam (ranged only). */
  jamChance: number;
}

/** Histogram bin for damage/crit distributions. */
export interface HistogramBin {
  min: number;
  max: number;
  count: number;
  probability: number;
}

export interface DamageDistribution {
  /** Histogram bins. */
  bins: HistogramBin[];
  /** Mean total damage per attack. */
  mean: number;
  /** The damage's variance over the runs: the fuzzer's odds check weighs a gap by it. */
  variance: number;
  /** Median total damage. */
  median: number;
  /** 95th percentile damage. */
  p95: number;
  /** Min damage observed. */
  min: number;
  /** Max damage observed. */
  max: number;
}

export interface CritDistribution {
  /** Probability of no crit at all. */
  noCritChance: number;
  /** Probability of at least one crit. */
  anyCritChance: number;
  /** Distribution of max crit severity in attacks that crit.
   *  Index = severity (1-10), value = probability. [0] is unused. */
  severityProbs: number[];
}

export interface WoundsToKillEstimate {
  /** Average attacks to kill. */
  meanAttacks: number;
  /** Survival probability after N attacks. survivalCurve[0] = 1.0. */
  survivalCurve: number[];
  /** Max attacks simulated. */
  maxAttacks: number;
}

/** Mirror of the live attack-DoS adjustment - keep in lockstep with useAttackExecution. */
function attackDos(roll: number, modifiedSkill: number, input: ProbabilityInput): number {
  const dos = applyUnnaturalDos(
    calcDoS(modifiedSkill, roll),
    roll,
    modifiedSkill,
    input.skillCharValue ?? 0,
    input.skillUnnaturalMultiplier ?? 1,
    input.unnaturalDosMode ?? DEFAULT_UNNATURAL_DOS_MODE,
  ).dos;
  // The target's `dos` exposure (v8.350.0) - mirrored from the live path.
  return exposedDos(dos, input.target, exposureReader(input));
}

/** The attacker as the target's exposures see it - the same reader the live path builds. */
function exposureReader(input: ProbabilityInput) {
  return {
    teamTag: input.attackerConditions?.teamTag ?? null,
    sideTag: input.attackerConditions?.sideTag ?? null,
    auras: input.attackerConditions?.auras,
    isMelee: input.isMelee,
  };
}

export interface ProbabilityInput {
  slot: Slot;
  target: Target;
  /** The attack is a charge (v8.368.2), for a `charge` exposure on the target. */
  charge?: boolean;
  attackMod: number;
  /** v7.0.0 - the acting combatant's conditions, so forecasts match live rolls. */
  attackerConditions?: ConditionModContext;
  /** House-rule gate; mirrors the flag the live attack path honours. */
  applyConditionMods?: boolean;
  /** Which of those modifiers the table has switched off; mirrors it too. */
  condOptions?: ConditionModOptions;
  rangeBand: string;
  fireMode: string;
  isMelee: boolean;
  hatred: boolean;
  frenzied: boolean;
  damageBonusMelee?: number;
  damageBonusRanged?: number;
  /** Unnatural DoS house rule - MUST mirror the live attack math (useAttackExecution). */
  unnaturalDosMode?: UnnaturalDosMode;
  /** Attacker governing characteristic's native value (AttackerCombatStats.skillCharValue). */
  skillCharValue?: number;
  /** Attacker governing characteristic's ×N (AttackerCombatStats.skillUnnaturalMultiplier). */
  skillUnnaturalMultiplier?: number;
  /** Whether Aim (half or full) is toggled - gates Accurate's +10 and bonus dice (v8.197.14). */
  aimed?: boolean;
  /** The re-roll damage pill: the dice rolled twice, the better kept - as the roll does (v8.390.3). */
  rerollDamage?: boolean;
  /**
   * Pin every hit to this location id instead of rolling one - the
   * firing-solutions board asks "what if I called THIS zone?" (v8.249.0).
   * An unknown id falls back to the rolled location.
   */
  forcedLocation?: string;
  /**
   * The forecast's own dice (see withForecastDice): every die a simulation
   * rolls - to-hit, location, damage - comes from here. Defaults to a fresh
   * generator on a fixed seed; the fuzz odds check injects its own.
   */
  rng?: () => number;
}

export interface ProbabilityResult {
  hits: HitDistribution;
  damage: DamageDistribution;
  crits: CritDistribution;
  woundsToKill: WoundsToKillEstimate;
}

// ────────────────────────────────────────────────────────────
// Configuration
// ────────────────────────────────────────────────────────────

const DAMAGE_ITERATIONS = 1000;
const WTK_COMBATS = 500;
const WTK_MAX_ATTACKS = 30;
const DAMAGE_BIN_COUNT = 15;
/** The forecast's default seed. Its value is arbitrary; that it is FIXED is the point. */
const FORECAST_SEED = 0x5eed;

// ────────────────────────────────────────────────────────────
// The forecast's own dice
// ────────────────────────────────────────────────────────────

/**
 * Run a Monte Carlo forecast on dice of its own (2026-10-02).
 *
 * The simulations roll through the live burst loop, which draws its dice
 * from the dice channel - the channel E2E and the fuzzer's replays seed
 * for the REAL rolls (CLAUDE.md gotcha 5). A forecast recomputes whenever
 * the setup changes, so drawing from that channel ate the seeded sequence
 * and shifted every later roll. Here the channel is swapped for the
 * forecast's own source for the call's duration (synchronous, so no real
 * roll can interleave), and the same source rides `input.rng` for the
 * to-hit roll.
 *
 * Inputs: the forecast input; `input.rng` when the caller injects one (the
 * fuzz odds check), else a fresh generator on FORECAST_SEED - so a setup
 * always forecasts the same numbers, and every export draws the same
 * sequence (the firing-solutions board's common random numbers, v8.250.1).
 * Output: whatever `body` returns, given the input with that source set.
 */
export function withForecastDice<I extends { rng?: () => number }, T>(input: I, body: (seeded: I) => T): T {
  const rng = input.rng ?? mulberry32(FORECAST_SEED);
  return withDiceSource(rng, () => body({ ...input, rng }));
}

// ────────────────────────────────────────────────────────────
// Analytical: Hit probability distribution
// ────────────────────────────────────────────────────────────

/** An `autoHit` exposure on the target (v8.368.2): the attack lands without a roll, as a Spray does. */
function autoHitAgainst(input: ProbabilityInput): boolean {
  return attackGate(input.target, exposureReader(input)).autoHit;
}

/**
 * The attack modifier INCLUDING condition penalties - one place, mirrored
 * from the live path in useAttackExecution. Recomputing these per call
 * site is how a forecast silently drifts from the roll it forecasts
 * (same rule applyUnnaturalDos established). The defence forecast reads it
 * too, so the defender's view of the attack is the same sum (2026-10-20).
 */
export function attackSkillMod(input: {
  attackMod: number;
  attackerConditions?: ConditionModContext;
  applyConditionMods?: boolean;
  condOptions?: ConditionModOptions;
  target: Target;
  isMelee: boolean;
  charge?: boolean;
  /** The band the shot is at, for a `range` exposure (Illuminated): one band closer is worth the step. */
  rangeBand?: string | null;
}): number {
  if (!input.applyConditionMods) return input.attackMod;
  const mods = getAttackConditionModifiers(
    input.attackerConditions ?? { conditions: [] },
    input.target as unknown as ConditionModContext,
    input.isMelee,
    input.condOptions,
    { charge: !!input.charge, rangeBand: input.rangeBand },
  );
  return input.attackMod + mods.total;
}

/** What every simulation of one attack shares, before any die is rolled. */
interface AttackSetup {
  modifiedSkill: number;
  /** What the target is exposed to on the roll (v8.368.1) - the same read the live path makes. */
  exposure: OnHitExposure;
  jamThreshold: number;
  /** The mode the hits are counted in and its cap - the roll's own rule (hitMode). */
  hitMode: { effectiveMode: string; rof: number };
  /**
   * SPRAY IS AN AUTO-HIT (v8.276.0): the live path never rolls a d100 and
   * never jams (useAttackExecution passes attackRoll 0), so the forecast
   * must not show odds the attack cannot produce. Same synthetic DoS,
   * same hit pipeline. An `autoHit` exposure does the same.
   */
  autoHit: boolean;
  /** The target is helpless against this attack (engine/helpless): a failed roll still lands, and damage is rolled twice. */
  helpless: boolean;
}

function attackSetup(input: ProbabilityInput): AttackSetup {
  const { slot, target, rangeBand, isMelee, hatred, frenzied } = input;
  const attackMod = attackSkillMod(input);
  const aimed = input.aimed ?? false;
  const modifiedSkill = calcModifiedSkill(
    slot,
    attackMod,
    target.size,
    rangeBand,
    isMelee,
    hatred,
    frenzied,
    false,
    false,
    aimed,
  );
  const exposure = onHitExposure(target, exposureReader(input));
  const jamThreshold = calcJamThreshold(slot.specialRules || [], isMelee, false, { jamFloor: exposure.jam });
  const autoHit = areaOf(slot).autoHit || autoHitAgainst(input);
  const helpless = helplessAgainst(input.target, isMelee, input.condOptions);
  return {
    modifiedSkill,
    exposure,
    jamThreshold,
    hitMode: hitMode(input.fireMode, slot, isMelee, false),
    autoHit,
    helpless,
  };
}

/** How many hits an attack with this DoS scores. */
function hitsFor(dos: number, input: ProbabilityInput, setup: AttackSetup): number {
  const { slot, target, rangeBand, isMelee } = input;
  const { effectiveMode, rof } = setup.hitMode;
  return calcTotalHits(dos, effectiveMode, rof, slot.specialRules || [], rangeBand, {
    isMelee,
    isHorde: target.horde?.isHorde || false,
    shift: setup.exposure.hits,
    attackerMagnitude: attackingMagnitude(input.attackerConditions),
  }).hits;
}

/** hitCount → how many of the 100 d100 faces produce it, and how many jam. */
function tallyRolls(input: ProbabilityInput, setup: AttackSetup): { freq: Map<number, number>; jamCount: number } {
  const freq = new Map<number, number>();
  const add = (hits: number) => freq.set(hits, (freq.get(hits) || 0) + 1);
  let jamCount = 0;
  for (let roll = 1; roll <= 100; roll++) {
    if (roll >= setup.jamThreshold) {
      jamCount++;
      add(0);
    } else if (roll > setup.modifiedSkill && !setup.helpless) {
      add(0);
    } else {
      // A failed roll against a helpless target still lands (engine/helpless): DoS floors at one.
      // Hit - compute DoS (incl. unnatural house rule) and total hits
      add(hitsFor(attackDos(roll, setup.modifiedSkill, input), input, setup));
    }
  }
  return { freq, jamCount };
}

function toProbabilities(freq: Map<number, number>): { probabilities: number[]; expectedHits: number } {
  const maxHits = Math.max(...freq.keys(), 0);
  const probabilities = new Array(maxHits + 1).fill(0);
  let expectedHits = 0;
  for (const [hits, count] of freq) {
    probabilities[hits] = count / 100;
    expectedHits += hits * (count / 100);
  }
  return { probabilities, expectedHits };
}

/**
 * Luck (v8.368.5), exactly: a Doomed target's miss is rolled again, so the
 * miss mass squares; a Charmed one's hit is rolled again, so the miss mass
 * is what two rolls fail to beat. The hit buckets keep their shape.
 */
function applyLuck(dist: { probabilities: number[]; expectedHits: number }, luck: number) {
  const { probabilities } = dist;
  if (luck === 0 || probabilities.length <= 1) return dist;
  const miss = probabilities[0] || 0;
  const missNow = luck > 0 ? miss * miss : 1 - (1 - miss) * (1 - miss);
  const scale = miss < 1 ? (1 - missNow) / (1 - miss) : 0;
  probabilities[0] = missNow;
  let expectedHits = 0;
  for (let h = 1; h < probabilities.length; h++) {
    probabilities[h] *= scale;
    expectedHits += h * probabilities[h];
  }
  return { probabilities, expectedHits };
}

/**
 * Compute exact P(N hits) by iterating all 100 d100 outcomes.
 * Each outcome is equally likely (1/100), so we just count.
 */
export function computeHitDistribution(input: ProbabilityInput): HitDistribution {
  const setup = attackSetup(input);
  if (setup.autoHit) {
    const hits = hitsFor(attackDos(0, setup.modifiedSkill, input), input, setup);
    const probabilities = new Array(hits + 1).fill(0);
    probabilities[hits] = 1;
    return { probabilities, expectedHits: hits, hitChance: 1, jamChance: 0 };
  }
  const { freq, jamCount } = tallyRolls(input, setup);
  const { probabilities, expectedHits } = applyLuck(toProbabilities(freq), setup.exposure.luck);
  return { probabilities, expectedHits, hitChance: 1 - (probabilities[0] || 0), jamChance: jamCount / 100 };
}

// ────────────────────────────────────────────────────────────
// Monte Carlo: Damage distribution
// ────────────────────────────────────────────────────────────

/** `iterations` simulated attacks on the forecast's own dice, one sample each, in order. */
function forecastSamples(
  input: ProbabilityInput,
  iterations: number,
  simulate: (seeded: ProbabilityInput) => number,
): number[] {
  return withForecastDice(input, (seeded) => Array.from({ length: iterations }, () => simulate(seeded)));
}

/**
 * Simulate N attacks, collecting total effective damage per attack.
 * Reuses calcSingleHitDamage directly for full special-rule fidelity.
 */
export function computeDamageDistribution(
  input: ProbabilityInput,
  iterations: number = DAMAGE_ITERATIONS,
): DamageDistribution {
  const samples = forecastSamples(input, iterations, simulateOneAttack).sort((a, b) => a - b);

  const mean = samples.reduce((s, v) => s + v, 0) / samples.length;
  const variance = samples.reduce((s, v) => s + (v - mean) ** 2, 0) / samples.length;
  const median = samples[Math.floor(samples.length / 2)];
  const p95 = samples[Math.floor(samples.length * 0.95)];
  const min = samples[0];
  const max = samples[samples.length - 1];

  // Build histogram bins
  const bins = buildHistogram(samples, DAMAGE_BIN_COUNT);

  return { bins, mean, variance, median, p95, min, max };
}

// ────────────────────────────────────────────────────────────
// Monte Carlo: Critical severity distribution
// ────────────────────────────────────────────────────────────

/**
 * Simulate attacks and collect max crit severity per attack.
 */
export function computeCritDistribution(
  input: ProbabilityInput,
  iterations: number = DAMAGE_ITERATIONS,
): CritDistribution {
  let noCritCount = 0;
  const sevCounts = new Array(11).fill(0); // [0..10], index 0 unused

  for (const maxSev of forecastSamples(input, iterations, simulateOneAttackCritSeverity)) {
    if (maxSev === 0) {
      noCritCount++;
    } else {
      sevCounts[maxSev]++;
    }
  }

  const noCritChance = noCritCount / iterations;
  const anyCritChance = 1 - noCritChance;
  const critAttacks = iterations - noCritCount;
  const severityProbs = sevCounts.map((c) => (critAttacks > 0 ? c / critAttacks : 0));

  return { noCritChance, anyCritChance, severityProbs };
}

// ────────────────────────────────────────────────────────────
// Monte Carlo: Wounds-to-kill estimate
// ────────────────────────────────────────────────────────────

/**
 * Simulate repeated attacks against the target until wounds <= 0.
 * Returns survival curve (probability target survives N attacks).
 */
export function computeWoundsToKill(
  input: ProbabilityInput,
  combats: number = WTK_COMBATS,
  maxAttacks: number = WTK_MAX_ATTACKS,
): WoundsToKillEstimate {
  const wounds = input.target.wounds.current;
  return withForecastDice(input, (seeded) =>
    survivalEstimate(() => simulateOneAttack(seeded), wounds, combats, maxAttacks),
  );
}

/**
 * Fight `combats` combats of up to `maxAttacks` attacks each, every attack
 * dealing what `attack()` returns, against a target starting at `wounds`.
 * Shared by the attack forecast and the defence forecast (which passes an
 * attack that is defended first). `meanAttacks` counts a survived combat
 * as `maxAttacks`, and is `maxAttacks` when nothing ever died.
 */
export function survivalEstimate(
  attack: () => number,
  wounds: number,
  combats: number,
  maxAttacks: number,
): WoundsToKillEstimate {
  const killCounts = new Array(maxAttacks + 1).fill(0); // killCounts[n] = combats where target died on attack n
  let totalAttacks = 0;
  let killCount = 0;
  for (let c = 0; c < combats; c++) {
    const killedOn = attackOfDeath(attack, wounds, maxAttacks);
    if (killedOn !== null) {
      killCounts[killedOn]++;
      killCount++;
    }
    totalAttacks += killedOn ?? maxAttacks;
  }
  const meanAttacks = killCount > 0 ? totalAttacks / killCount : maxAttacks;
  return { meanAttacks, survivalCurve: survivalCurveOf(killCounts, combats, maxAttacks), maxAttacks };
}

/** One combat: the attack (1-based) that brought the target to 0 wounds, or null if it lasted them all. */
function attackOfDeath(attack: () => number, wounds: number, maxAttacks: number): number | null {
  let remainingWounds = wounds;
  for (let atk = 1; atk <= maxAttacks; atk++) {
    remainingWounds -= attack();
    if (remainingWounds <= 0) return atk;
  }
  return null;
}

/** survivalCurve[n] = the share of combats the target survived n attacks; [0] = 1.0. */
function survivalCurveOf(killCounts: number[], combats: number, maxAttacks: number): number[] {
  const survivalCurve = new Array(maxAttacks + 1).fill(0);
  survivalCurve[0] = 1.0; // 100% alive before any attack
  let cumKilled = 0;
  for (let i = 1; i <= maxAttacks; i++) {
    cumKilled += killCounts[i];
    survivalCurve[i] = 1 - cumKilled / combats;
  }
  return survivalCurve;
}

// ────────────────────────────────────────────────────────────
// Combined computation
// ────────────────────────────────────────────────────────────

/**
 * Compute all probability results for a given input configuration.
 * Analytical hit distribution + Monte Carlo for damage, crits, WTK.
 */
export function computeProbabilities(input: ProbabilityInput): ProbabilityResult {
  return {
    hits: computeHitDistribution(input),
    damage: computeDamageDistribution(input),
    crits: computeCritDistribution(input),
    woundsToKill: computeWoundsToKill(input),
  };
}

// ────────────────────────────────────────────────────────────
// Internal helpers
// ────────────────────────────────────────────────────────────

/**
 * One simulated attack's hits, in order: the to-hit roll (from `input.rng`,
 * which withForecastDice sets), then per hit its location and damage. Empty on a
 * miss or a jam. A Spray auto-hits with the synthetic DoS and cannot jam
 * (the live path's attackRoll 0).
 */
function simulateAttackHits(input: ProbabilityInput): HitResult[] {
  return simulateAttack(input).hits;
}

/** Does this face of the d100 land nothing? A jam always; a failed roll unless the target is helpless. */
function missesOrJams(roll: number, setup: AttackSetup): boolean {
  if (setup.autoHit) return false;
  return roll >= setup.jamThreshold || (roll > setup.modifiedSkill && !setup.helpless);
}

/** One simulated attack: its landed hits and its degrees of success (0 for a miss or a jam). */
function simulateAttack(input: ProbabilityInput): { hits: HitResult[]; dos: number } {
  const setup = attackSetup(input);
  const roll = setup.autoHit ? 0 : Math.floor((input.rng ?? Math.random)() * 100) + 1;
  if (missesOrJams(roll, setup)) return { hits: [], dos: 0 };
  const dos = attackDos(roll, setup.modifiedSkill, input);
  return { hits: rollHitDamages(input, dos, hitsFor(dos, input, setup)), dos };
}

/** What every hit of one simulated attack passes to calcSingleHitDamage. */
function sharedHitParams(input: ProbabilityInput, dos: number) {
  const { slot, target, rangeBand, fireMode, isMelee, hatred } = input;
  return {
    slot,
    target,
    pen: calcEffectivePen(slot.penetration, slot.specialRules || [], dos, rangeBand).pen,
    dos,
    accurateBonus: accurateBonusDice(slot.specialRules || [], fireMode, dos, input.aimed ?? false),
    rangeBand,
    isMelee,
    isShortOrCloser: rangeBand === 'pointBlank' || rangeBand === 'short',
    hatred,
    damageBonusMelee: input.damageBonusMelee ?? 0,
    damageBonusRanged: input.damageBonusRanged ?? 0,
    rerollDamage: input.rerollDamage ?? false,
    helpless: helplessAgainst(target, isMelee, input.condOptions),
    bodyType: target.bodyType,
  };
}

/**
 * Each hit's location (the first drawn, the rest by the Multiple Hits table, or the pinned zone) and damage through the live
 * burst loop, so wounds run down and an overloaded field stays down exactly
 * as they do at the table.
 */
function rollHitDamages(input: ProbabilityInput, dos: number, numHits: number): HitResult[] {
  const { target, forcedLocation } = input;
  const bodyConfig = getBodyConfig(target.bodyType || 'humanoid');
  const forcedLoc = forcedLocation ? bodyConfig.locations.find((l) => l.id === forcedLocation) : undefined;
  return rollBurst({
    shared: sharedHitParams(input, dos),
    target,
    numHits,
    wounds: target.wounds.current,
    // The first hit drawn, the rest by the Multiple Hits table - as the roll (a called location takes them all).
    locationAt: forcedLoc ? () => forcedLoc : burstLocations(bodyConfig),
  }).hits;
}

/**
 * Simulate a single full attack (roll to hit, compute damage for each hit).
 * Returns total effective damage dealt to the target.
 */
function simulateOneAttack(input: ProbabilityInput): number {
  const { hits, dos } = simulateAttack(input);
  const landed = hits.reduce((total, r) => total + r.effectiveDamage, 0);
  // What the roll adds after the hits - Toxic, Corrosive (v8.392.5).
  return landed + afterHitDamage(input.target, hits, dos, input.slot);
}

/**
 * Simulate a single attack and return the maximum crit severity
 * across all hits. Returns 0 if no crits occurred.
 */
function simulateOneAttackCritSeverity(input: ProbabilityInput): number {
  return simulateAttackHits(input).reduce((maxSev, r) => (r.critSev > maxSev ? r.critSev : maxSev), 0);
}

/** Build a histogram from sorted samples. */
export function buildHistogram(sorted: number[], binCount: number): HistogramBin[] {
  if (sorted.length === 0) return [];
  const min = sorted[0];
  const max = sorted[sorted.length - 1];
  // If all samples are the same value, return a single bin
  if (min === max) return [{ min, max, count: sorted.length, probability: 1 }];

  const binWidth = Math.max(1, Math.ceil((max - min + 1) / binCount));
  const bins = emptyBins(min, max, binWidth, binCount);
  for (const v of sorted) {
    const idx = Math.min(Math.floor((v - min) / binWidth), bins.length - 1);
    bins[idx].count++;
  }
  for (const bin of bins) bin.probability = bin.count / sorted.length;
  return bins;
}

/** Up to `binCount` bins of `binWidth` from `min`, the last clipped to `max`. */
function emptyBins(min: number, max: number, binWidth: number, binCount: number): HistogramBin[] {
  const bins: HistogramBin[] = [];
  for (let i = 0; i < binCount; i++) {
    const lo = min + i * binWidth;
    if (lo > max) break;
    bins.push({ min: lo, max: Math.min(lo + binWidth - 1, max), count: 0, probability: 0 });
  }
  return bins;
}

// ────────────────────────────────────────────────────────────
// Firing solutions - the board beside the targeting console
// ────────────────────────────────────────────────────────────

/** One called-shot option: what a hit through THIS zone is worth. */
interface ZoneSolution {
  id: string;
  name: string;
  /** Mean total damage per attack, hits pinned to this zone. */
  meanDamage: number;
}

export interface FiringSolutions {
  /** Location by roll - no called shot. */
  auto: { hitChance: number; jamChance: number; meanDamage: number };
  /**
   * The to-hit chance shared by EVERY called shot at this setup - the
   * penalty is flat across zones, so only the damage differs per row.
   * null when this body takes no called shots (horde / amorphous).
   */
  calledHitChance: number | null;
  calledShotPenalty: number;
  /** Empty for hordes (mass target) and amorphous bodies. */
  zones: ZoneSolution[];
}

/**
 * Outcome math for every aim option of the current setup.
 *
 * `input.attackMod` MUST be the base modifier WITHOUT an engaged
 * called-shot chip (the live attackMod folds it in while the toggle is
 * on - the caller backs it out); the penalty is applied here, per row.
 * Same body-type predicate as the targeting console: hordes are a mass
 * first, then `supportsCalledShot` rules amorphous bodies out.
 */
export function computeFiringSolutions(
  input: ProbabilityInput,
  calledShotPenalty: number,
  iterations: number = 400,
): FiringSolutions {
  const autoHits = computeHitDistribution(input);
  const auto = {
    hitChance: autoHits.hitChance,
    jamChance: autoHits.jamChance,
    meanDamage: seededMeanDamage(input, iterations),
  };

  const bodyConfig = getBodyConfig(input.target.bodyType || (input.target.horde?.isHorde ? 'horde' : 'humanoid'));
  const isHorde = input.target.bodyType === 'horde' || !!input.target.horde?.isHorde;
  if (isHorde || !bodyConfig.supportsCalledShot) {
    return { auto, calledHitChance: null, calledShotPenalty, zones: [] };
  }

  const calledInput: ProbabilityInput = { ...input, attackMod: input.attackMod - calledShotPenalty };
  const calledHitChance = computeHitDistribution(calledInput).hitChance;
  const zones = bodyConfig.locations.map((loc) => ({
    id: loc.id,
    name: loc.name,
    meanDamage: seededMeanDamage({ ...calledInput, forcedLocation: loc.id }, iterations),
  }));

  return { auto, calledHitChance, calledShotPenalty, zones };
}

/**
 * Common random numbers (v8.250.1): every row's simulation draws the SAME
 * seeded sequence (withForecastDice carries it to every die). Zones with
 * identical armour therefore report identical numbers, and the board does
 * not flicker between recomputes. A fresh generator per row, even when the
 * caller passed one, so no row continues another's stream.
 */
function seededMeanDamage(input: ProbabilityInput, iterations: number): number {
  return computeDamageDistribution({ ...input, rng: mulberry32(FORECAST_SEED) }, iterations).mean;
}
